import { ICS2EnsDeclarationPayload } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { Ics2ErrorLocation } from './types';

/**
 * The route suffix, relative to the declaration's base url, that opens the form holding an error.
 *
 * Levels are resolved against the declaration payload rather than the products listing on purpose:
 * a customs pointer indexes the message that was submitted, and that message was built from these
 * very arrays. The listing is fetched separately and carries no guaranteed order, so it would be
 * the wrong thing to count positions in.
 *
 * Returns undefined when the level cannot be resolved - an item that has since been deleted, or a
 * house consignment that was never saved - so the caller can leave the user where they are instead
 * of navigating somewhere arbitrary.
 */
export const resolveIcs2LocationRoute = (
    location: Ics2ErrorLocation,
    payload?: ICS2EnsDeclarationPayload
): string | undefined => {
    const master = payload?.consignmentMasterLevel;

    if (location.level === 'master') return '';

    if (location.level === 'masterItem') {
        const id = master?.goodsItem?.[toOffset(location.itemIndex)]?.id;

        return id ? `/master-items/${id}` : undefined;
    }

    const houseConsignment = master?.consignmentHouseLevel?.[toOffset(location.houseConsignmentIndex)];
    if (!houseConsignment?.id) return undefined;

    if (location.level === 'houseConsignment') return `/house-consignments/${houseConsignment.id}`;

    const itemId = houseConsignment.goodsItem?.[toOffset(location.itemIndex)]?.id;

    return itemId ? `/house-consignments/${houseConsignment.id}/${itemId}` : undefined;
};

/** Locations number levels the way the UI does, one ahead of the array they came from. */
const toOffset = (displayIndex?: number): number => (displayIndex ?? 1) - 1;
